import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Pages are client-rendered, so title/description/lang have to be updated in
// place on navigation and whenever the visitor switches language. `scope` is
// the locale key holding that page's { title, description }.
const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export function useSeo(scope = 'seo', { noindex = false } = {}) {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const isEnglish = (i18n.language || '').toLowerCase().startsWith('en');
    const title = t(`${scope}.title`);
    const description = t(`${scope}.description`);

    document.documentElement.lang = isEnglish ? 'en' : 'pl';
    document.title = title;

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:locale', isEnglish ? 'en_US' : 'pl_PL');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
  }, [t, i18n.language, scope]);

  // Pages that must stay out of the index (the 404) say so while mounted.
  useEffect(() => {
    if (!noindex) return undefined;
    const previous = document.head.querySelector('meta[name="robots"]')?.getAttribute('content');
    setMeta('name', 'robots', 'noindex');
    return () => {
      if (previous) setMeta('name', 'robots', previous);
      else document.head.querySelector('meta[name="robots"]')?.remove();
    };
  }, [noindex]);
}

export default useSeo;
